<template>
  <div class="min-h-screen relative overflow-hidden">
    <!-- Background Image with Dark Transparent Overlay -->
    <div
      class="absolute inset-0 bg-cover bg-center bg-no-repeat"
      :style="{ backgroundImage: `url(${backgroundImage})` }"
    ></div>
    <div class="absolute inset-0 bg-stone-950 bg-opacity-80"></div>

    <!-- Content -->
    <div class="relative z-10 min-h-screen flex items-center justify-center">
      <div class="w-full max-w-4xl">
        <!-- Card -->
        <div class="p-4 lg:p-2">
          <!-- Brand Logo -->
          <div class="text-center mb-8">
            <img
              v-if="currentBrand"
              :src="currentBrand.formLogo"
              :alt="currentBrand.name"
              class="mx-auto w-120 object-contain"
            />
            <h2 class="text-6xl font-medium" :style="{ color: currentBrand?.color }">
              {{ isFalken ? 'Road Hazard Ready' : 'TOTAL CONFIDENCE PLAN' }}
            </h2>
            <p class="mt-6 mb-4 text-2xl text-white">
              Create an Account
            </p>
            <div class="text-center">
                <p class="text-xl text-white">
                Already have an account?
                <a
                    href="#"
                    @click.prevent="goToSignIn"
                    class="font-medium hover:underline"
                    :style="{ color: currentBrand?.color }"
                >
                    Sign In
                </a>
                </p>
            </div>
          </div>

          <!-- Success Message -->
          <div v-if="showSuccess" class="mb-6 rounded-md bg-blue-50 p-6">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-8 w-8 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z" />
                  <path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z" />
                </svg>
              </div>
              <div class="ml-4">
                <h3 class="text-lg font-semibold text-blue-900">Registration Successful!</h3>
                <p class="mt-2 text-sm text-blue-800">
                  We've sent a verification email to <strong>{{ formData.email }}</strong>. Please check your inbox and click the verification link to activate your account.
                </p>
                <p class="mt-2 text-sm text-blue-700">
                  The link will expire in <strong>48 hours</strong>.
                </p>
                <p class="mt-2 text-xs text-blue-600">
                  If you don't see the email, please check your spam folder.
                </p>
                <div class="mt-4">
                  <button
                    @click="goToSignIn"
                    class="text-sm font-medium text-blue-700 hover:text-blue-900 underline"
                  >
                    Go to Login
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="mb-6 rounded-md bg-red-50 p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <p class="text-sm text-red-700">{{ error }}</p>
              </div>
            </div>
          </div>

          <!-- Sign Up Form -->
          <form v-if="!showSuccess" @submit.prevent="handleSignUp" class="space-y-6">
            <!-- First Name and Last Name in One Row -->
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <input
                  id="firstName"
                  v-model="formData.firstName"
                  type="text"
                  placeholder="First Name*"
                  required
                  class="w-full px-8 py-5 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                  :style="{
                    borderColor: currentBrand?.color,
                    color: 'white',
                    '--focus-color': currentBrand?.color
                  }"
                />
              </div>

              <div>
                <input
                  id="lastName"
                  v-model="formData.lastName"
                  type="text"
                  placeholder="Last Name*"
                  required
                  class="w-full px-8 py-5 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                  :style="{
                    borderColor: currentBrand?.color,
                    color: 'white',
                    '--focus-color': currentBrand?.color
                  }"
                />
              </div>
            </div>

            <!-- Email -->
            <div>
              <input
                id="email"
                v-model="formData.email"
                type="email"
                placeholder="Email Address*"
                required
                class="w-full px-8 py-5 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                :style="{
                  borderColor: currentBrand?.color,
                  color: 'white',
                  '--focus-color': currentBrand?.color
                }"
              />
            </div>

            <!-- Password -->
            <div class="relative">
              <input
                id="password"
                v-model="formData.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Password*"
                required
                class="w-full px-8 py-5 pr-14 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                :style="{
                  borderColor: currentBrand?.color,
                  color: 'white',
                  '--focus-color': currentBrand?.color
                }"
              />
              <button
                type="button"
                @click="showPassword = !showPassword"
                class="absolute inset-y-0 right-0 pr-4 flex items-center text-white hover:opacity-80 transition-opacity"
              >
                <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                  <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                  <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                  <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Confirm Password -->
            <div class="relative">
              <input
                id="confirmPassword"
                v-model="formData.confirmPassword"
                :type="showConfirmPassword ? 'text' : 'password'"
                placeholder="Confirm Password*"
                required
                class="w-full px-8 py-5 pr-14 bg-transparent border-2 rounded-lg focus:outline-none placeholder-white placeholder:text-lg brand-input"
                :style="{
                  borderColor: currentBrand?.color,
                  color: 'white',
                  '--focus-color': currentBrand?.color
                }"
              />
              <button
                type="button"
                @click="showConfirmPassword = !showConfirmPassword"
                class="absolute inset-y-0 right-0 pr-4 flex items-center text-white hover:opacity-80 transition-opacity"
              >
                <svg v-if="showConfirmPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                  <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l18 18a.75.75 0 1 0 1.06-1.06l-18-18ZM22.676 12.553a11.249 11.249 0 0 1-2.631 4.31l-3.099-3.099a5.25 5.25 0 0 0-6.71-6.71L7.759 4.577a11.217 11.217 0 0 1 4.242-.827c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113Z" />
                  <path d="M15.75 12c0 .18-.013.357-.037.53l-4.244-4.243A3.75 3.75 0 0 1 15.75 12ZM12.53 15.713l-4.243-4.244a3.75 3.75 0 0 0 4.244 4.243Z" />
                  <path d="M6.75 12c0-.619.107-1.213.304-1.764l-3.1-3.1a11.25 11.25 0 0 0-2.63 4.31c-.12.362-.12.752 0 1.114 1.489 4.467 5.704 7.69 10.675 7.69 1.5 0 2.933-.294 4.242-.827l-2.477-2.477A5.25 5.25 0 0 1 6.75 12Z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                  <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" />
                  <path fill-rule="evenodd" d="M1.323 11.447C2.811 6.976 7.028 3.75 12.001 3.75c4.97 0 9.185 3.223 10.675 7.69.12.362.12.752 0 1.113-1.487 4.471-5.705 7.697-10.677 7.697-4.97 0-9.186-3.223-10.675-7.69a1.762 1.762 0 0 1 0-1.113ZM17.25 12a5.25 5.25 0 1 1-10.5 0 5.25 5.25 0 0 1 10.5 0Z" clip-rule="evenodd" />
                </svg>
              </button>
            </div>

            <!-- Acknowledgment & Consent Checkbox -->
            <div
              class="rounded-lg border-2 p-5"
              :style="{ borderColor: currentBrand?.color + '66' }"
            >
              <p
                class="text-sm font-semibold mb-3"
                :style="{ color: currentBrand?.color }"
              >
                Acknowledgment and Consent
              </p>
              <label class="flex items-start gap-3 cursor-pointer group">
                <!-- Custom Checkbox -->
                <div class="relative flex-shrink-0 mt-0.5">
                  <input
                    id="consentCheckbox"
                    v-model="consentChecked"
                    type="checkbox"
                    class="sr-only"
                  />
                  <div
                    class="w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200"
                    :style="{
                      borderColor: currentBrand?.color,
                      backgroundColor: consentChecked ? currentBrand?.color : 'transparent'
                    }"
                  >
                    <svg
                      v-if="consentChecked"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      class="w-3 h-3"
                      :style="{ color: currentBrand?.buttonTextColor || '#fff' }"
                    >
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                <!-- Consent Text -->
                <span class="text-sm text-white leading-relaxed opacity-90">
                  By registering, I confirm that I have read and agree to the
                  <span class="font-semibold" :style="{ color: currentBrand?.color }">
                    {{ isFalken ? 'Road Hazard Ready (RHR)' : 'Continental Total Confidence Plan (TCP)' }} Terms and Conditions
                  </span>.
                  I consent to the collection, use, processing, and storage of my personal data in
                  accordance with the
                  <span class="font-semibold" :style="{ color: currentBrand?.color }">Data Privacy Act of 2012 (Republic Act No. 10173)</span>
                  and applicable regulations, for purposes related to warranty registration, verification,
                  claims processing, customer support, and marketing communications.
                  I understand that I may withdraw my consent or opt out of marketing communications
                  at any time.
                </span>
              </label>

              <!-- Inline warning when submit attempted without checking -->
              <p
                v-if="consentError"
                class="mt-3 text-sm text-red-400 flex items-center gap-1"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-4 h-4 flex-shrink-0">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                </svg>
                You must accept the Terms and Conditions to proceed.
              </p>
            </div>

            <!-- Submit Button -->
            <div class="flex justify-center">
              <button
                type="submit"
                :disabled="loading"
                class="px-16 mb-12 py-3 font-medium text-xl rounded-full hover:opacity-90 transition-opacity"
                :style="{
                  backgroundColor: currentBrand?.buttonColor,
                  color: currentBrand?.buttonTextColor,
                  opacity: loading ? 0.7 : 1
                }"
              >
                {{ loading ? 'Creating Account...' : 'SIGN UP' }}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getBrandBySlug } from '@/config/brands'
import api from '@/services/api'

const route = useRoute()
const router = useRouter()

const currentBrand = computed(() => {
  const brandSlug = route.params.brandSlug as string
  return getBrandBySlug(brandSlug)
})

const isFalken = computed(() => currentBrand.value?.name?.toLowerCase() === 'falken')

// eslint-disable-next-line @typescript-eslint/no-var-requires
const backgroundImage = require('@/assets/staticbg.png')

const formData = ref({
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: ''
})

const loading = ref(false)
const error = ref('')
const showSuccess = ref(false)
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const consentChecked = ref(false)
const consentError = ref(false)

onMounted(() => {
  // Redirect to brand selection if no brand is selected
  if (!currentBrand.value) {
    router.push({ name: 'brand-selection' })
  }
})

const handleSignUp = async () => {
  // Validate consent checkbox first
  if (!consentChecked.value) {
    consentError.value = true
    return
  }
  consentError.value = false

  try {
    loading.value = true
    error.value = ''

    // Validate passwords match
    if (formData.value.password !== formData.value.confirmPassword) {
      throw new Error('Passwords do not match')
    }

    // Validate password length
    if (formData.value.password.length < 8) {
      throw new Error('Password must be at least 8 characters long')
    }

    // Call registration API
    await api.post('/auth/register', {
      brand_id: currentBrand.value?.id,
      first_name: formData.value.firstName,
      last_name: formData.value.lastName,
      email: formData.value.email,
      password: formData.value.password,
      password_confirmation: formData.value.confirmPassword
    })

    const registeredEmail = formData.value.email
    
    showSuccess.value = true

    formData.value = {
      firstName: '',
      lastName: '',
      email: registeredEmail,
      password: '',
      confirmPassword: ''
    }

  } catch (err: any) {
    console.error('Registration error:', err)

    // Handle validation errors from Laravel
    if (err.response?.data?.errors) {
      const errors = err.response.data.errors
      const firstError = Object.values(errors)[0] as string[]
      error.value = firstError[0]
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message
    } else if (err.message) {
      error.value = err.message
    } else {
      error.value = 'Registration failed. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const goToSignIn = () => {
  router.push({
    name: 'login',
    params: { brandSlug: currentBrand.value?.slug }
  })
}
</script>

<style scoped>
/* Custom focus styles for inputs */
.brand-input:focus {
  outline: none;
  border-width: 2px;
  border-color: var(--focus-color) !important;
  box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.1);
}

/* Placeholder styling */
input::placeholder {
  color: white;
  opacity: 0.7;
}

input:focus::placeholder {
  opacity: 0.5;
}
</style>